import { useState, useEffect, useCallback, useRef } from 'react';
import * as Sentry from "@sentry/react";
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { useAuth } from '@/contexts/AuthContext';
import { AnswerData } from '@/components/assessment/conversational/answerTypes';
import { useToast } from '@/hooks/use-toast';
import { config } from '@/config/environment';
import { getAssessmentIdByModuleId } from '@/utils/assessmentLookup';
import { logger } from '@/utils/logger';

const STORAGE_KEY = 'assessment_progress';

interface AssessmentProgress {
  questionIndex: number;
  answers: AnswerData;
  lastSaved: string;
}

interface SaveIndividualResponseResult {
  questionKey: string;
  resolvedQuestionId?: string | null;
  error?: string;
}

// Cache for question IDs to avoid repeated lookups
const questionIdCache = new Map<string, string>();

export function useAssessmentPersistence(moduleId: string, assessmentId?: string) {
  const [resolvedDefaultId, setResolvedDefaultId] = useState<string | null>(null);

  // Resolve A1 UUID dynamically on mount when no explicit assessmentId is provided
  useEffect(() => {
    if (assessmentId) return;
    let cancelled = false;
    getAssessmentIdByModuleId('owner-readiness').then((id) => {
      if (!cancelled) setResolvedDefaultId(id);
    });
    return () => { cancelled = true; };
  }, [assessmentId]);

  const effectiveAssessmentId = assessmentId || resolvedDefaultId;

  const { user, session } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [teamId, setTeamId] = useState<string | null>(null);
  
  // Refs for sendBeacon data persistence
  const assessmentDataRef = useRef<{
    questionIndex: number;
    answers: AnswerData;
  } | null>(null);
  const userIdRef = useRef<string | null>(null);
  const authTokenRef = useRef<string | null>(null);

  // Fetch user's team_id on mount
  useEffect(() => {
    const fetchTeamId = async () => {
      if (!user?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('team_members')
          .select('team_id')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .limit(1)
          .single();
        
        // Fall back to userId as synthetic teamId (mirrors scoring engine)
        setTeamId(!error && data ? data.team_id : user.id);
      } catch (err) {
        console.warn('[useAssessmentPersistence] Team lookup failed — persistence will not work:', err);
        toast({ title: "Session error", description: "Could not load team data. Please refresh the page.", variant: "destructive" });
      }
    };
    
    fetchTeamId();
  }, [user?.id]);

  // Load progress from localStorage on mount
  const loadLocalProgress = useCallback((): AssessmentProgress | null => {
    try {
      const stored = localStorage.getItem(`${STORAGE_KEY}_${moduleId}`);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'storage_error', operation: 'load_progress' },
        extra: { moduleId }
      });
      
      toast({
        title: "Failed to Load Progress",
        description: "Your saved assessment data couldn't be loaded. Starting fresh.",
        variant: "destructive"
      });
      
      return null;
    }
  }, [moduleId]);

  // Save progress to localStorage
  const saveLocalProgress = useCallback((progress: AssessmentProgress) => {
    try {
      localStorage.setItem(`${STORAGE_KEY}_${moduleId}`, JSON.stringify(progress));
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'storage_error', operation: 'save_progress' },
        extra: { moduleId, dataSize: JSON.stringify(progress).length }
      });
      
      toast({
        title: "Failed to Save Progress",
        description: "Your changes couldn't be saved locally. Please ensure you have enough storage space.",
        variant: "destructive"
      });
      
      throw error;
    }
  }, [moduleId]);


  // Debounced localStorage save for typing inputs
  const debouncedLocalSave = useCallback((questionIndex: number, answers: AnswerData) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      const progress: AssessmentProgress = {
        questionIndex,
        answers,
        lastSaved: new Date().toISOString()
      };
      saveLocalProgress(progress);
    }, 500); // 500ms debounce
  }, [saveLocalProgress]);

  // Update assessment data in refs for sendBeacon
  const updateAssessmentData = useCallback((questionIndex: number, answers: AnswerData) => {
    assessmentDataRef.current = { questionIndex, answers };
    userIdRef.current = user?.id || null;
  }, [user?.id]);

  // Keep auth token updated in ref for sendBeacon
  useEffect(() => {
    authTokenRef.current = session?.access_token || null;
  }, [session]);

  // Get question UUID from question_key (with caching)
  const getQuestionId = useCallback(async (questionKey: string): Promise<string | null> => {
    // Check cache first
    if (questionIdCache.has(questionKey)) {
      return questionIdCache.get(questionKey)!;
    }

    try {
      const { data, error } = await supabase
        .from('assessment_questions')
        .select('id')
        .eq('question_key', questionKey)
        .eq('assessment_id', effectiveAssessmentId)
        .single();

      if (error || !data) {
        console.warn(`Question not found in database: ${questionKey}`);
        return null;
      }

      // Cache the result
      questionIdCache.set(questionKey, data.id);
      return data.id;
    } catch (error) {
      console.error('Error fetching question ID:', error);
      return null;
    }
  }, [effectiveAssessmentId]);

  // Save individual response to user_assessment_responses
  const saveIndividualResponse = useCallback(async (
    questionKey: string,
    answerValue: unknown,
    resolvedAssessmentIdOverride?: string | null
  ): Promise<SaveIndividualResponseResult> => {
    if (!user?.id) {
      return { questionKey, error: 'No authenticated user available' };
    }
    
    // Skip if answer is empty/undefined
    if (answerValue === undefined || answerValue === null || answerValue === '') {
      return { questionKey };
    }

    // Use override (synchronously resolved) if provided, otherwise fall back to hook state
    const activeAssessmentId = resolvedAssessmentIdOverride ?? effectiveAssessmentId;

    if (!activeAssessmentId) {
      logger.warn(`[saveIndividualResponse] No assessment ID available for question "${questionKey}" — skipping`);
      return { questionKey, error: 'No assessment ID available' };
    }

    // Query question_id using the active assessment ID directly (bypass getQuestionId closure)
    let questionId: string | null = questionIdCache.get(questionKey) ?? null;
    if (!questionId) {
      try {
        const { data, error } = await supabase
          .from('assessment_questions')
          .select('id')
          .eq('question_key', questionKey)
          .eq('assessment_id', activeAssessmentId)
          .single();
        if (!error && data) {
          questionIdCache.set(questionKey, data.id);
          questionId = data.id;
        }
      } catch (err) {
        console.error('Error fetching question ID:', err);
      }
    }

    if (!questionId) {
      console.warn(`Cannot save response: question_key "${questionKey}" not found in database`);
      return {
        questionKey,
        resolvedQuestionId: null,
        error: `Question lookup returned null for ${questionKey}`,
      };
    }

    try {
      const { error } = await supabase
        .from('user_assessment_responses')
        .upsert({
          user_id: user.id,
          team_id: teamId,
          assessment_id: activeAssessmentId,
          question_id: questionId,
          response_value: answerValue as unknown as Json,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,question_id'
        });

      if (error) {
        console.error('Error saving individual response:', error);
        return {
          questionKey,
          resolvedQuestionId: questionId,
          error: String(error.message || error),
        };
      }

      return {
        questionKey,
        resolvedQuestionId: questionId,
      };
    } catch (error) {
      console.error('Error saving individual response:', error);
      return {
        questionKey,
        resolvedQuestionId: questionId,
        error: String(error),
      };
    }
  }, [user?.id, teamId, effectiveAssessmentId, moduleId]);

  // Save all changed answers to individual responses
  const saveAllResponses = useCallback(async (answers: AnswerData) => {
    if (!user?.id) return;

    // Resolve assessment ID synchronously from cache — don't rely on hook state
    const resolvedId = effectiveAssessmentId || await getAssessmentIdByModuleId(moduleId);

    const savePromises = Object.entries(answers).map(([questionKey, answerValue]) => 
      saveIndividualResponse(questionKey, answerValue, resolvedId)
    );

    await Promise.allSettled(savePromises);
  }, [user?.id, teamId, effectiveAssessmentId, moduleId, saveIndividualResponse]);

  // Auto-save function (called after each answer) - only saves to localStorage with debounce
  const autoSave = useCallback((questionIndex: number, answers: AnswerData) => {
    debouncedLocalSave(questionIndex, answers);
    updateAssessmentData(questionIndex, answers);
  }, [debouncedLocalSave, updateAssessmentData]);

  // Save to database (for Next, Save Progress, completion, unload)
  const saveToDatabase = useCallback(async (questionIndex: number, answers: AnswerData, showToast: boolean = false) => {
    const progress: AssessmentProgress = {
      questionIndex,
      answers,
      lastSaved: new Date().toISOString()
    };

    // Save to localStorage immediately
    saveLocalProgress(progress);

    if (!user) return;

    setIsSaving(true);
    try {
      // Calculate total questions count for completion percentage
      const totalQuestions = Object.keys(answers).length || 20; // Default fallback
      const completionPercentage = Math.min(100, Math.round((progress.questionIndex / totalQuestions) * 100));
      
      // Save to assessment_progress (for resume functionality)
      const { error } = await supabase
        .from('assessment_progress')
        .upsert({
          user_id: user.id,
          module_id: moduleId,
          answers: progress.answers as unknown as Json,
          current_question_index: progress.questionIndex,
          total_questions: totalQuestions,
          completion_percentage: completionPercentage,
          is_complete: completionPercentage >= 100,
          last_active_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,module_id',
        });

      if (error) {
        throw error;
      }

      // Also save individual responses to user_assessment_responses
      // This runs in the background, don't block on it
      saveAllResponses(answers).catch(err => {
        console.error('Error saving individual responses:', err);
        Sentry.captureException(err, {
          tags: { context: 'individual_responses', component: 'useAssessmentPersistence' }
        });
      });

      if (showToast) {
        toast({
          title: "Progress saved",
          description: "Your assessment progress has been saved.",
        });
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'database_save', component: 'useAssessmentPersistence' },
        extra: { message: 'Failed to save to database', moduleId, questionIndex }
      });
      
      if (showToast) {
        toast({
          title: "Save failed",
          description: "Your progress couldn't be saved. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSaving(false);
    }
  }, [user, moduleId, saveLocalProgress, toast, saveAllResponses]);

  // Manual save function (shows toast)
  const manualSave = useCallback(async (questionIndex: number, answers: AnswerData) => {
    await saveToDatabase(questionIndex, answers, true);
  }, [saveToDatabase]);

  // Clear progress
  const clearProgress = useCallback(() => {
    localStorage.removeItem(`${STORAGE_KEY}_${moduleId}`);
  }, [moduleId]);

  // Save on page unload/refresh using sendBeacon
  useEffect(() => {
    const handleBeforeUnload = () => {
      const currentData = assessmentDataRef.current;
      const userId = userIdRef.current;
      const token = authTokenRef.current;
      
      if (currentData && userId && token) {
        try {
          // Prepare beacon payload with auth token included
          const payload = JSON.stringify({
            moduleId,
            questionIndex: currentData.questionIndex,
            answers: currentData.answers,
            totalQuestions: Object.keys(currentData.answers).length || 20,
            authToken: token // Include token in payload since sendBeacon can't send custom headers
          });

          // Create blob with proper content type
          const blob = new Blob([payload], { type: 'application/json' });
          
          // Get Supabase project URL
          const supabaseUrl = config.supabase.url;
          const beaconUrl = `${supabaseUrl}/functions/v1/save-assessment-beacon`;
          
          // Use sendBeacon for guaranteed delivery
          navigator.sendBeacon(beaconUrl, blob);
        } catch (error) {
          // Silently fail - unload event
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [moduleId]);

  return {
    loadLocalProgress,
    autoSave,
    manualSave,
    saveToDatabase,
    saveAllResponses,
    clearProgress,
    updateAssessmentData,
    saveIndividualResponse,
    isLoading,
    isSaving,
    teamId,
    assessmentId: effectiveAssessmentId
  };
}
